import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  MapPin, Car, ShieldCheck, Calendar,
  ScanSearch, Heart, Phone, Mail, Building2, ArrowLeft, Star,
  FileCheck2, LockKeyhole, Eye, MessageCircle, Expand, X, CheckCircle2,
  ClipboardCheck, UserCheck, BadgeCheck, ArrowRight, Share2, Clock3, CloudSun, Loader2,
  Fence, Lock, Zap, Droplet, Waves, Route, ArrowUpDown, Dumbbell,
  Shield, Battery, Video, Coffee, Sprout, CornerUpRight, Home
} from "lucide-react";
import { api, formatPrice, formatArea, formatApiError, BACKEND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useCompare } from "@/contexts/CompareContext";
import { useAuth } from "@/contexts/AuthContext";
import { useShortlist } from "@/contexts/ShortlistContext";
import PropertyCard from "@/components/PropertyCard";
import PropertyContextMap from "@/components/property/PropertyContextMap";
import PropertyMap from "@/components/property/PropertyMap";
import PropertyThreadPanel from "@/components/PropertyThreadPanel";
import { InlineVerificationBadges } from "@/components/TrustStrip";
import EmptyState from "@/components/EmptyState";
import StatusBadge from "@/components/StatusBadge";
import { buyerUnavailableReason, isPendingSoldStatus, isSoldStatus } from "@/lib/propertyUi";

// Phone authentication stays unavailable until the backend has a verified SMS transport.
import { PHONE_AUTH_ENABLED } from "@/lib/authConfig";
const TIME_SLOTS = ["10:00 AM", "11:30 AM", "1:00 PM", "3:00 PM", "5:00 PM"];
const REQUIRED_LAND_DOCS = ["registry", "jamabandi", "khatiyan", "mutation", "khasra", "survey_number", "ownership_proof", "tax_receipt"];
const DOC_LABELS = {
  registry: "Registry",
  jamabandi: "Jamabandi",
  khatiyan: "Khatiyan",
  mutation: "Mutation",
  khasra: "Khasra",
  survey_number: "Survey No.",
  ownership_proof: "Ownership Proof",
  tax_receipt: "Tax/MCD Receipt"
};
const AMENITY_ICONS = {
  "Boundary Wall": Fence,
  "Gated Colony": Lock,
  "Electricity": Zap,
  "Water": Droplet,
  "Drainage": Waves,
  "Road Access": Route,
  "Parking": Car,
  "Lift": ArrowUpDown,
  "Gym": Dumbbell,
  "Swimming Pool": Waves,
  "Security": Shield,
  "Power Backup": Battery,
  "CCTV": Video,
  "Club House": Coffee,
  "Garden": Sprout,
};

export default function PropertyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [activeImg, setActiveImg] = useState(0);
  const { ids: compareIds, toggle: toggleCompare } = useCompare();
  const { user } = useAuth();
  const { isSaved, toggle: toggleSave } = useShortlist();
  const [bookingOpen, setBookingOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.state && typeof location.state.scrollY === "number") {
      setTimeout(() => {
        window.scrollTo({
          top: location.state.scrollY,
          behavior: "smooth"
        });
      }, 350);
    }
  }, [location.state]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError("");
    api.get(`/web/property/${id}`)
      .then(({ data }) => {
        if (live) {
          setData(data);
          try {
            const list = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
            const filtered = list.filter((x) => x !== id);
            filtered.unshift(id);
            localStorage.setItem("recently_viewed_properties", JSON.stringify(filtered.slice(0, 15)));
          } catch (e) {
            console.error(e);
          }
        }
      })
      .catch((requestError) => {
        if (live) {
          setData(null);
          setLoadError(requestError?.response?.status === 404 ? "not_found" : formatApiError(requestError));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, reloadKey]);

  if (loading) return <DetailSkeleton />;
  if (!data && loadError && loadError !== "not_found") return <div className="max-w-4xl mx-auto px-6 py-20"><EmptyState title="Property could not be loaded" description={loadError} primaryLabel="Back to listings" primaryTo="/properties" secondaryLabel="Try again" onSecondary={() => setReloadKey((key) => key + 1)} testid="property-load-error" /></div>;
  if (!data) return <EmptyState title="Property not found" description="This listing may be unpublished, archived, or unavailable." primaryLabel="Back to listings" primaryTo="/properties" />;

  const p = data.property || data.data || data;
  const agent = data.agent || p.agent || null;
  const office = data.office || p.office || null;
  const previewMode = Boolean(data.preview_mode || p.preview_mode);
  const previewRole = String(data.preview_role || p.preview_role || "").toUpperCase();
  const previewMessage = data.preview_message || p.preview_message || "Preview mode: this listing is not publicly visible right now.";
  const similar = data.similar || p.similar || [];
  const reviews = data.reviews || p.reviews || [];
  const images = normalizeImages(p);
  const land = p.land_details || p.land || {};
  const building = p.building_details || p.building || {};
  const isLand = ["LAND", "PLOT"].includes(String(p.category || "").toUpperCase());
  // Purpose: direct detail/saved links can still open sold properties, but buyer actions must be disabled.
  const unavailableReason = buyerUnavailableReason(p.status);
  const showSoldBanner = isSoldStatus(p.status) || isPendingSoldStatus(p.status);
  const backTo = location.state?.from || location.state?.searchUrl || "/properties";
  const similarSearchUrl = `/properties?${new URLSearchParams({
    ...(p.city ? { city: p.city } : {}),
    ...(p.category ? { category: p.category } : {}),
  }).toString()}`;
  const similarMapMarkers = [p, ...similar].map((item, index) => {
    const propertyId = String(item.property_id || item.id || "");
    return {
      ...item,
      id: propertyId,
      isCurrent: index === 0,
      href: propertyId ? `/properties/${propertyId}` : undefined,
    };
  });

  const saved = isSaved(p.property_id || id);

  const handleSave = () => {
    toggleSave(p.property_id || id);
  };

  const handleShare = async () => {
    const shareData = { title: p.title, text: `${p.title} on ShreeHomes`, url: window.location.href };
    try {
      if (navigator.share) await navigator.share(shareData);
      else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareData.url);
        toast.success("Property link copied");
      } else {
        toast.error("Sharing is not available in this browser");
      }
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Could not share this property");
    }
  };

  return (
    <div className="premium-page-surface bg-transparent pb-20 lg:pb-0" data-testid="property-detail-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6 lg:py-8">
        <div className="mb-4 flex items-center justify-between gap-3 sm:mb-6">
          <Link to={backTo} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary sm:text-sm" data-testid="back-link"><ArrowLeft className="w-4 h-4"/>Back to listings</Link>
          <nav className="hidden items-center gap-1.5 text-xs font-semibold text-muted-foreground sm:flex" aria-label="Breadcrumb" data-testid="property-breadcrumb">
            <Link to="/properties" className="hover:text-primary">Buy</Link>
            {p.city && <><span>/</span><Link to={`/properties?city=${encodeURIComponent(p.city)}`} className="hover:text-primary">{p.city}</Link></>}
            {p.locality && <><span>/</span><Link to={`/properties?${new URLSearchParams({ city: p.city || "", locality: p.locality }).toString()}`} className="hover:text-primary">{p.locality}</Link></>}
          </nav>
        </div>

        <ImageGallery images={images} title={p.title} activeImg={activeImg} setActiveImg={setActiveImg} />

        {showSoldBanner && (
          <div className="mt-4 sm:mt-6 rounded-2xl border border-warning/30 bg-warning/10 p-4 text-warning-foreground" data-testid="sold-status-banner">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest">Availability update</p>
                <p className="text-sm font-semibold mt-1">{unavailableReason}</p>
              </div>
              <StatusBadge status={p.status} />
            </div>
          </div>
        )}

        {previewMode && (
          <div className="mt-4 sm:mt-6 rounded-2xl border border-primary/30 bg-primary/10 p-4 text-primary" data-testid="preview-mode-banner">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest">Preview mode</p>
                <p className="text-sm font-semibold mt-1">{previewMessage}</p>
              </div>
              <div className="inline-flex items-center rounded-full border border-primary/30 bg-background px-3 py-1 text-xs font-bold uppercase tracking-wide">
                {previewRole || "AUTHORIZED"}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 mt-6 sm:mt-10">
          <main className="lg:col-span-8 min-w-0 space-y-6 sm:space-y-10">
            <section>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2 inline-flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{[p.locality, p.city, p.state].filter(Boolean).join(", ")}</p>
                  <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-primary leading-[1.15] sm:leading-tight" data-testid="prop-title">{p.title}</h1>
                  <InlineVerificationBadges property={p} className="mt-3.5" />
                </div>
                <div className="flex gap-2 shrink-0 mt-2 sm:mt-0">
                  <button type="button" onClick={() => { if (!user) toast.error("Please log in to book a site visit"); else setBookingOpen(true); }} className="w-10 h-10 grid place-items-center rounded-full border border-border text-primary hover:bg-secondary transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="book-visit-top-btn" title="Book site visit" aria-label="Book a site visit">
                    <Calendar className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={handleSave} className={`w-10 h-10 grid place-items-center rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${saved ? "bg-primary text-primary-foreground border-primary" : "border-border text-primary hover:bg-secondary"}`} data-testid="save-btn" title={saved ? "Remove from saved properties" : "Save property"} aria-label={saved ? "Remove from saved properties" : "Save property"}><Heart className="w-4 h-4" fill={saved ? "currentColor" : "none"}/></button>
                  <button type="button" onClick={() => toggleCompare(p.property_id)} className={`w-10 h-10 grid place-items-center rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${compareIds.includes(p.property_id) ? "bg-primary text-primary-foreground border-primary" : "border-border text-primary hover:bg-secondary"}`} data-testid="compare-btn" title={compareIds.includes(p.property_id) ? "Remove from comparison" : "Add to comparison"} aria-label={compareIds.includes(p.property_id) ? "Remove from comparison" : "Add to comparison"}><ScanSearch className="w-4 h-4"/></button>
                  <button type="button" onClick={handleShare} className="w-10 h-10 grid place-items-center rounded-full border border-border text-primary hover:bg-secondary transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="share-btn" title="Share property" aria-label="Share property"><Share2 className="w-4 h-4"/></button>
                </div>
              </div>
              <div className="mt-6 border-y border-border py-5">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                  <div>
                    <p className="font-display text-3xl sm:text-4xl font-black text-primary tabular-nums leading-none" data-testid="prop-price">{formatPrice(p.price)}</p>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1.5">{p.category || "Property"} · {p.listing_type || p.new_or_resale || "—"}</p>
                  </div>
                  <div className="hidden h-10 w-px bg-border sm:block" aria-hidden="true" />
                  <BigStat value={formatArea(p.area || p.area_sqft || land.area || building.area, p.area_unit || land.area_unit || building.area_unit)} label="Area" />
                  {!isLand && (p.bedrooms || building.bedrooms) > 0 && <BigStat value={p.bedrooms || building.bedrooms} label="Beds" />}
                  {!isLand && (p.bathrooms || building.bathrooms) > 0 && <BigStat value={p.bathrooms || building.bathrooms} label="Baths" />}
                  {isLand && (p.road_width_ft || land.road_width_ft) > 0 && <BigStat value={`${p.road_width_ft || land.road_width_ft} ft`} label="Road Width" />}
                  {(p.facing || building.facing || land.facing) && (p.facing || building.facing || land.facing) !== "—" && <BigStat value={p.facing || building.facing || land.facing} label="Facing" />}
                </div>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  {(p.parking || building.parking || building.parking_spaces > 0) ? <Spec icon={Car} v={building.parking_spaces > 0 ? `${building.parking_spaces} slots` : "Yes"} l="Parking"/> : null}
                  {isLand && (p.corner_plot || land.corner_plot) && (p.corner_plot === true || p.corner_plot === "true" || land.corner_plot === true || land.corner_plot === "true") && <Spec icon={CornerUpRight} v="Yes" l="Corner Plot"/>}
                  {!isLand && (building.furnishing_status || building.furnishing || p.furnishing) && (building.furnishing_status || building.furnishing || p.furnishing) !== "—" && <Spec icon={Home} v={building.furnishing_status || building.furnishing || p.furnishing} l="Furnishing"/>}
                  <Spec icon={Eye} v={p.view_count || p.views || 0} l="Views"/>
                </div>
              </div>
            </section>

            <PropertyStickyNav />

            <section id="overview" className="scroll-mt-32" data-testid="overview-section">
              <h3 className="font-display text-xl font-bold text-primary mb-4">About this property</h3>
              <ExpandableText text={p.description} />
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {p.category && p.category !== "—" && <Detail label="Category" value={p.category}/>}
                {p.listing_type && p.listing_type !== "—" && <Detail label="Listing" value={p.listing_type}/>}
                {(land.facing || building.facing || p.facing) && (land.facing || building.facing || p.facing) !== "—" && <Detail label="Facing" value={land.facing || building.facing || p.facing}/>}
                {(land.ownership_type || p.ownership) && (land.ownership_type || p.ownership) !== "—" && <Detail label="Ownership" value={land.ownership_type || p.ownership}/>}
                {!isLand && building.furnishing && building.furnishing !== "—" && <Detail label="Furnishing" value={building.furnishing}/>} 
                {!isLand && (building.property_age || p.age_years) && (building.property_age || p.age_years) !== "—" && <Detail label="Age" value={`${building.property_age || p.age_years} yrs`}/>} 
                {p.status && p.status !== "—" && <Detail label="Status" value={String(p.status).replace(/_/g, " ")}/>}
                {(p.property_code || p.property_id) && (p.property_code || p.property_id) !== "—" && <Detail label="Code" value={p.property_code || p.property_id}/>}
              </div>
            </section>

            <section id="neighborhood" className="scroll-mt-32 border-t border-border pt-8 premium-card bg-card border border-border rounded-2xl p-4 sm:p-6" data-testid="property-location-section">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Neighborhood</p>
              <h3 className="mt-1 font-display text-2xl font-bold text-primary">Around this property</h3>
              <p className="mb-5 mt-1 text-sm text-muted-foreground">Explore the current property with nearby ShreeHomes listings. Location overlays remain separate from property inventory.</p>
              <PropertyContextMap property={p} nearbyProperties={similar} />
              {p.nearby && Object.keys(p.nearby).length > 0 && <div className="mt-5" data-testid="nearby-connectivity-section"><NearbyInfo nearby={p.nearby} /></div>}
            </section>

            <section id="details" className="scroll-mt-32 border-t border-border pt-8" data-testid="legal-section">
              <h3 className="font-display text-xl font-bold text-primary mb-4">{isLand ? "Land & Legal Details" : "Building Details"}</h3>
              {isLand ? <LandLegalDetails land={land} /> : <BuildingDetails building={building} />}
            </section>

            <section className="border-t border-border pt-8" data-testid="amenities-section">
              <h3 className="font-display text-xl font-bold text-primary mb-4">Amenities</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(p.amenities || []).map((a, idx) => {
                  const IconComponent = AMENITY_ICONS[a] || ShieldCheck;
                  return (
                    /* Amenity chip: staggered entrance + animated gradient icon that bobs on hover */
                    <div key={a} style={{ animationDelay: `${Math.min(idx, 12) * 50}ms`, animationFillMode: "both" }} className="animate-fade-up border border-border rounded-xl px-4 py-3 text-sm font-semibold text-primary flex items-center gap-2 hover:border-primary/30 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 group" data-testid={`amenity-${a}`}>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500/15 to-emerald-400/5 ring-1 ring-emerald-500/15 text-emerald-600 group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300 shrink-0 animate-gradient-pan">
                        <IconComponent className="w-4 h-4 group-hover:animate-icon-bob"/>
                      </span>
                      <span>{a}</span>
                    </div>
                  );
                })}
                {(p.amenities || []).length === 0 && <p className="text-muted-foreground">No amenities listed.</p>}
              </div>
            </section>

            <section id="documents" className="scroll-mt-32" data-testid="documents-section">
              <VerificationPanel property={p} />
            </section>

            <section id="price-history" className="scroll-mt-32 border-t border-border pt-8" data-testid="price-history-section">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-secondary text-primary"><Clock3 className="h-5 w-5" /></span>
                <div><p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Price & History</p><h3 className="font-display text-xl font-bold text-primary">Listing timeline</h3></div>
              </div>
              <PropertyHistory property={p} />
            </section>

            <section id="environment" className="scroll-mt-32 border-t border-border pt-8" data-testid="environment-section">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-secondary text-primary"><CloudSun className="h-5 w-5" /></span>
                <div><p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Environment</p><h3 className="font-display text-xl font-bold text-primary">Environment & location risk</h3></div>
              </div>
              <p className="mt-4 rounded-xl border border-dashed border-border bg-secondary/20 p-4 text-sm text-muted-foreground">No provider-backed environment data is available for this property. ShreeHomes does not estimate or invent risk scores.</p>
            </section>

            <section id="similar" className="scroll-mt-32 border-t border-border pt-8" data-testid="similar-properties-section">
              <div className="mb-6 flex items-end justify-between gap-4">
                <div><p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Nearby alternatives</p><h3 className="mt-1 font-display text-2xl font-bold text-primary">Similar properties</h3></div>
                <Link to={similarSearchUrl} className="inline-flex shrink-0 items-center gap-1 text-sm font-bold text-primary hover:underline">See all <ArrowRight className="h-4 w-4" /></Link>
              </div>
              {similar?.length > 0 ? <>
                <PropertyMap markers={similarMapMarkers} selectedMarkerId={p.property_id || p.id} height={320} className="mb-6 rounded-2xl border border-border" testId="similar-properties-map" />
                <div className="grid sm:grid-cols-2 gap-6">
                  {similar.slice(0,4).map(s => <PropertyCard key={s.property_id} property={s}/>) }
                </div>
              </> : <p className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">No similar published properties are available yet.</p>}
            </section>

            <PropertyThreadPanel propertyId={p.property_id} />

            {/* Section 5: Reviews (Shown at the very end of the page) */}
            <section className="border-t border-border pt-8" data-testid="reviews-section">
              <h3 className="font-display text-xl font-bold text-primary mb-4">User Reviews ({reviews?.length || 0})</h3>
              <ReviewSection propertyId={p.property_id} initial={reviews}/>
            </section>
          </main>

          <aside className="lg:col-span-4 min-w-0">
            <div className="sticky top-24 max-h-[calc(100vh-8.5rem)] overflow-y-auto pr-1.5 scrollbar-thin">
              <ListingAgentLeadCard property={p} agent={agent} office={office} unavailableReason={unavailableReason} bookingOpen={bookingOpen} setBookingOpen={setBookingOpen}/>
            </div>
          </aside>
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t border-border bg-background/95 p-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur lg:hidden" data-testid="mobile-property-actions">
        <Button type="button" variant="outline" onClick={() => document.querySelector('[data-testid="contact-privacy-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}><MessageCircle className="mr-2 h-4 w-4" />Contact Agent</Button>
        <Button type="button" onClick={() => { if (!user) toast.error("Please log in to book a site visit"); else setBookingOpen(true); }} disabled={!!unavailableReason}><Calendar className="mr-2 h-4 w-4" />Schedule Visit</Button>
      </div>
    </div>
  );
}

const PROPERTY_SECTIONS = [
  ["overview", "Overview"],
  ["neighborhood", "Neighborhood"],
  ["details", "Details"],
  ["documents", "Documents"],
  ["price-history", "Price & History"],
  ["environment", "Environment"],
  ["similar", "Similar"],
];

function PropertyStickyNav() {
  const [active, setActive] = useState("overview");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const sections = PROPERTY_SECTIONS.map(([sectionId]) => document.getElementById(sectionId)).filter(Boolean);
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible?.target?.id) setActive(visible.target.id);
    }, { rootMargin: "-28% 0px -60% 0px", threshold: [0.05, 0.25, 0.5] });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <nav className="sticky top-16 z-30 -mx-1 overflow-x-auto border-b border-border bg-background/95 px-1 shadow-sm backdrop-blur" aria-label="Property sections" data-testid="property-sticky-nav">
      <div className="flex min-w-max gap-1">
        {PROPERTY_SECTIONS.map(([sectionId, label]) => (
          <button
            key={sectionId}
            type="button"
            onClick={() => { setActive(sectionId); document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            className={`relative whitespace-nowrap px-3.5 py-3 text-[13px] font-bold transition-colors ${active === sectionId ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
            aria-current={active === sectionId ? "location" : undefined}
          >
            {label}
            <span className={`absolute inset-x-2.5 bottom-0 h-[3px] rounded-t-full transition-opacity ${active === sectionId ? "bg-primary opacity-100" : "opacity-0"}`} aria-hidden="true" />
          </button>
        ))}
      </div>
    </nav>
  );
}

function PropertyHistory({ property }) {
  const propertyId = property.property_id || property.id;
  const [history, setHistory] = useState({ listing_history: [], transaction_history: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const publishedAt = property.published_at || property.created_at;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.get(`/web/property/${encodeURIComponent(propertyId)}/history`)
      .then(({ data }) => {
        if (!active) return;
        setHistory({
          listing_history: Array.isArray(data?.listing_history) ? data.listing_history : [],
          transaction_history: Array.isArray(data?.transaction_history) ? data.transaction_history : [],
        });
      })
      .catch((requestError) => {
        if (!active) return;
        setError(formatApiError(requestError) || "Property history could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [propertyId]);

  const listingEvents = history.listing_history;
  const transactionEvents = history.transaction_history;
  return (
    <div className="mt-5 space-y-4">
      {loading && <p className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading property history...</p>}
      {error && <p className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning-foreground" role="alert">{error}</p>}
      <div className="divide-y divide-border rounded-xl border border-border">
        {!loading && listingEvents.length > 0 ? listingEvents.map((event, index) => (
          <div key={`${event.event_type}-${event.event_at}-${index}`} className="grid grid-cols-[1fr_auto] gap-4 p-4">
            <div><p className="font-bold text-primary">{listingEventLabel(event.event_type)}</p><p className="text-xs text-muted-foreground">{formatDate(event.event_at)}</p></div>
            {(event.new_price || event.old_price) && <p className="font-display font-black text-primary">{formatPrice(event.new_price || event.old_price)}</p>}
          </div>
        )) : !loading && publishedAt ? <div className="grid grid-cols-[1fr_auto] gap-4 p-4"><div><p className="font-bold text-primary">Listed</p><p className="text-xs text-muted-foreground">{formatDate(publishedAt)}</p></div><p className="font-display font-black text-primary">{formatPrice(property.price)}</p></div> : null}
        <div className="grid grid-cols-[1fr_auto] gap-4 p-4"><div><p className="font-bold text-primary">Current asking price</p><p className="text-xs text-muted-foreground">Current listing projection</p></div><p className="font-display font-black text-primary">{formatPrice(property.price)}</p></div>
      </div>
      <div className="rounded-xl border border-border p-4">
        <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Verified transactions</p>
        {transactionEvents.length > 0 ? <div className="mt-3 divide-y divide-border">{transactionEvents.map((transaction, index) => (
          <div key={`${transaction.transaction_type}-${transaction.transaction_date}-${index}`} className="grid grid-cols-[1fr_auto] gap-4 py-3">
            <div><p className="font-bold text-primary">Verified {String(transaction.transaction_type).toLowerCase()}</p><p className="text-xs text-muted-foreground">{formatDate(transaction.transaction_date)}</p></div>
            <p className="font-display font-black text-primary">{formatPrice(transaction.transaction_price)}</p>
          </div>
        ))}</div> : !loading && <p className="mt-2 text-sm text-muted-foreground">No verified transaction history is available for this property.</p>}
      </div>
    </div>
  );
}

function listingEventLabel(eventType) {
  return ({
    LISTED: "Listed",
    PRICE_CHANGED: "Price changed",
    STATUS_CHANGED: "Listing status changed",
    WITHDRAWN: "Listing withdrawn",
    SOLD: "Listing marked sold",
  })[eventType] || "Listing updated";
}

function ImageGallery({ images, title, activeImg, setActiveImg }) {
  const [isOpen, setIsOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && activeImg < images.length - 1) {
      setActiveImg((prev) => prev + 1);
    }
    if (isRightSwipe && activeImg > 0) {
      setActiveImg((prev) => prev - 1);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (activeImg > 0) setActiveImg((prev) => prev - 1);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (activeImg < images.length - 1) setActiveImg((prev) => prev + 1);
  };

  const openDialogAt = (index) => {
    setActiveImg(index);
    setIsOpen(true);
  };

  const renderSlider = (isFullscreen = false) => (
    <div 
      className={`relative w-full aspect-[16/10] sm:aspect-[16/9] overflow-hidden bg-slate-950 group select-none ${
        isFullscreen ? "rounded-2xl max-h-[75vh]" : "rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.12)] border border-slate-200/40"
      }`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") handlePrev();
        if (event.key === "ArrowRight") handleNext();
      }}
      tabIndex={images.length > 1 ? 0 : undefined}
      aria-label={`${title} image gallery. Use the left and right arrow keys to change images.`}
    >
      {/* Sliding images wrapper */}
      <div 
        className="flex h-full w-full transition-transform duration-500"
        style={{ transform: `translateX(-${activeImg * 100}%)`, transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
      >
        {images.map((img, i) => (
          <div key={i} className="w-full h-full shrink-0 relative select-none">
            <img 
              src={img} 
              alt={`${title} - ${i + 1}`} 
              className={propertyImageClass(img)}
              draggable={false}
            />
          </div>
        ))}
      </div>

      {/* Floating Number Indicator */}
      <span className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md px-3.5 py-1.5 rounded-full text-xs font-black tracking-wide text-white z-20 shadow-lg border border-white/10 select-none">
        {activeImg + 1} / {images.length}
      </span>

      {/* Left Arrow Button */}
      {activeImg > 0 && (
        <button
          type="button"
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/75 text-white grid place-items-center backdrop-blur-sm z-20 shadow-md border border-white/5 transition-all duration-300 hover:scale-105 active:scale-95"
          aria-label="Previous image"
        >
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Right Arrow Button */}
      {activeImg < images.length - 1 && (
        <button
          type="button"
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/75 text-white grid place-items-center backdrop-blur-sm z-20 shadow-md border border-white/5 transition-all duration-300 hover:scale-105 active:scale-95"
          aria-label="Next image"
        >
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Fullscreen Button */}
      {!isFullscreen && (
        <button 
          type="button"
          onClick={() => setIsOpen(true)}
          className="absolute bottom-4 left-4 rounded-full bg-black/60 backdrop-blur-md px-4 py-2 text-xs font-black uppercase tracking-wider text-white shadow-lg border border-white/10 hover:bg-black/80 transition-all duration-300 inline-flex items-center gap-1.5 z-20"
          aria-label="Open full-screen property gallery"
        >
          <Expand className="h-3.5 w-3.5" /> Fullscreen
        </button>
      )}
    </div>
  );

  return (
    <div className="w-full" data-testid="gallery">
      {/* Mobile view: Swipeable Slider directly inline */}
      <div className="block lg:hidden w-full">
        {renderSlider(false)}
        
        {/* Horizontal Thumbnails Scroll list */}
        <div className="flex gap-2.5 overflow-x-auto py-2 scrollbar-none justify-start w-full mt-3" data-testid="thumbnails">
          {images.map((img, i) => (
            <button
              type="button"
              key={`${img}-${i}`}
              onClick={() => setActiveImg(i)}
              className={`relative w-20 h-14 rounded-2xl overflow-hidden border-2 shrink-0 transition-all duration-300 ${
                activeImg === i 
                  ? "border-primary scale-[1.04] shadow-[0_4px_15px_rgba(0,120,219,0.15)]" 
                  : "border-slate-200/50 opacity-70 hover:opacity-100 hover:scale-[1.02]"
              }`}
              data-testid={`thumb-${i}`}
              aria-label={`Show image ${i + 1} of ${images.length}`}
              aria-current={activeImg === i ? "true" : undefined}
            >
              <img src={img} alt="" loading="lazy" decoding="async" className={propertyImageClass(img)} />
            </button>
          ))}
        </div>
      </div>

      {/* Desktop view: Adaptive Grid Gallery */}
      <div className="hidden lg:grid grid-cols-4 gap-3 h-[420px] w-full overflow-hidden rounded-3xl" data-testid="gallery-desktop">
        {images.length === 1 && (
          <div role="button" tabIndex={0} className="col-span-4 w-full h-full rounded-3xl overflow-hidden relative group cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openDialogAt(0)} onKeyDown={(e) => e.key === "Enter" && openDialogAt(0)} aria-label="Open image 1 in full-screen gallery">
            <img src={images[0]} alt={title} decoding="async" className={propertyImageClass(images[0], "transition-transform duration-750 group-hover:scale-[1.015]")} />
          </div>
        )}
        {images.length === 2 && (
          <>
            <div role="button" tabIndex={0} className="col-span-2 w-full h-full rounded-l-3xl overflow-hidden relative group cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openDialogAt(0)} onKeyDown={(e) => e.key === "Enter" && openDialogAt(0)} aria-label="Open image 1 in full-screen gallery">
              <img src={images[0]} alt={title} decoding="async" className={propertyImageClass(images[0], "transition-transform duration-750 group-hover:scale-[1.015]")} />
            </div>
            <div role="button" tabIndex={0} className="col-span-2 w-full h-full rounded-r-3xl overflow-hidden relative group cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openDialogAt(1)} onKeyDown={(e) => e.key === "Enter" && openDialogAt(1)} aria-label="Open image 2 in full-screen gallery">
              <img src={images[1]} alt={title} decoding="async" className={propertyImageClass(images[1], "transition-transform duration-750 group-hover:scale-[1.015]")} />
            </div>
          </>
        )}
        {images.length === 3 && (
          <>
            <div role="button" tabIndex={0} className="col-span-2 w-full h-full rounded-l-3xl overflow-hidden relative group cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openDialogAt(0)} onKeyDown={(e) => e.key === "Enter" && openDialogAt(0)} aria-label="Open image 1 in full-screen gallery">
              <img src={images[0]} alt={title} decoding="async" className={propertyImageClass(images[0], "transition-transform duration-750 group-hover:scale-[1.015]")} />
            </div>
            <div className="col-span-2 grid grid-rows-2 gap-3 h-full">
              <div role="button" tabIndex={0} className="w-full h-full rounded-tr-3xl overflow-hidden relative group cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openDialogAt(1)} onKeyDown={(e) => e.key === "Enter" && openDialogAt(1)} aria-label="Open image 2 in full-screen gallery">
                <img src={images[1]} alt={title} loading="lazy" decoding="async" className={propertyImageClass(images[1], "transition-transform duration-750 group-hover:scale-[1.015]")} />
              </div>
              <div role="button" tabIndex={0} className="w-full h-full rounded-br-3xl overflow-hidden relative group cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openDialogAt(2)} onKeyDown={(e) => e.key === "Enter" && openDialogAt(2)} aria-label="Open image 3 in full-screen gallery">
                <img src={images[2]} alt={title} loading="lazy" decoding="async" className={propertyImageClass(images[2], "transition-transform duration-750 group-hover:scale-[1.015]")} />
              </div>
            </div>
          </>
        )}
        {images.length >= 4 && (
          <>
            {/* Left main image */}
            <div role="button" tabIndex={0} className="col-span-2 w-full h-full rounded-l-3xl overflow-hidden relative group cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openDialogAt(0)} onKeyDown={(e) => e.key === "Enter" && openDialogAt(0)} aria-label="Open image 1 in full-screen gallery">
              <img src={images[0]} alt={title} decoding="async" className={propertyImageClass(images[0], "transition-transform duration-750 group-hover:scale-[1.015]")} />
            </div>
            {/* Right side grid */}
            <div className="col-span-2 grid grid-cols-2 grid-rows-2 gap-3 h-full">
              {images.slice(1, 5).map((img, i) => {
                const roundedClass = 
                  i === 1 ? "rounded-tr-3xl" :
                  i === 3 ? "rounded-br-3xl" : "";
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={i} 
                    className={`relative w-full h-full overflow-hidden cursor-pointer group rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${roundedClass}`}
                    onClick={() => openDialogAt(i + 1)}
                    onKeyDown={(e) => e.key === "Enter" && openDialogAt(i + 1)}
                    aria-label={`Open image ${i + 2} in full-screen gallery`}
                  >
                    <img src={img} alt="" loading="lazy" decoding="async" className={propertyImageClass(img, "transition-transform duration-750 group-hover:scale-[1.015]")} />
                    {i === 3 && images.length > 5 && (
                      <div className="absolute inset-0 bg-black/55 hover:bg-black/65 transition-all flex flex-col items-center justify-center text-white text-base font-black uppercase tracking-wider">
                        <span>+ {images.length - 5} More</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Shared Fullscreen Lightbox Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-5xl p-2 bg-slate-950 border-slate-900" data-testid="gallery-fullscreen-dialog">
          <DialogHeader className="sr-only"><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="relative w-full bg-slate-950 flex flex-col gap-2">
            {renderSlider(true)}
            {/* Fullscreen Thumbnails List */}
            <div className="flex gap-2 overflow-x-auto py-1.5 scrollbar-none justify-start w-full">
              {images.map((img, i) => (
                <button
                  type="button"
                  key={`${img}-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`relative w-16 h-11 rounded-xl overflow-hidden border-2 shrink-0 transition-all duration-300 ${
                    activeImg === i ? "border-primary scale-[1.04]" : "border-slate-800 opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`Show image ${i + 1} of ${images.length}`}
                  aria-current={activeImg === i ? "true" : undefined}
                >
                  <img src={img} alt="" loading="lazy" decoding="async" className={propertyImageClass(img)} />
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VerificationPanel({ property }) {
  const docs = Array.isArray(property.documents) ? property.documents : [];
  const uploaded = new Set(docs.map(d => String(d.document_type || "").toLowerCase()));
  const completion = REQUIRED_LAND_DOCS.filter(d => uploaded.has(d)).length;
  return (
    <section className="rounded-2xl border border-border bg-gradient-to-br from-emerald-50 to-background p-5" data-testid="document-verification-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">Legal/document verification</p>
          <h2 className="font-display text-xl font-black text-primary mt-1">{property.verified_documents ? "Documents verified" : "Document review in progress"}</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">Tracked: Registry, Mutation, Khatiyan, Ownership Proof, Tax Receipt.</p>
        </div>
        <div className="rounded-2xl bg-background px-5 py-3 text-center shadow-sm border border-border shrink-0">
          <p className="font-display text-2xl font-black text-primary">{completion}/{REQUIRED_LAND_DOCS.length}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Docs uploaded</p>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {REQUIRED_LAND_DOCS.map((doc) => {
          const ok = uploaded.has(doc) || property.verified_documents;
          const displayLabel = DOC_LABELS[doc] || doc.replace(/_/g, " ");
          return (
            <span 
              key={doc} 
              className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[10px] font-bold ${
                ok 
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200/50" 
                  : "bg-secondary/20 text-muted-foreground border border-border"
              }`}
            >
              <FileCheck2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{displayLabel}</span>
            </span>
          );
        })}
      </div>
    </section>
  );
}

function ListingAgentLeadCard({ property, agent, office, unavailableReason, bookingOpen, setBookingOpen }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [revealed, setRevealed] = useState(false);
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(`I'm interested in ${property.title || "this property"}.`);
  const masked = agent?.phone || property.owner_phone || property.contact_phone
    ? maskPhone(agent?.phone || property.owner_phone || property.contact_phone)
    : "Protected contact";
  const isUnavailable = !!unavailableReason;

  // Phone verification overlay state
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [dialogPhone, setDialogPhone] = useState("");
  const [dialogOtp, setDialogOtp] = useState("");
  const [dialogOtpSent, setDialogOtpSent] = useState(false);
  const [postVerificationAction, setPostVerificationAction] = useState(null); // "reveal" | "whatsapp"

  const handleLoginRedirect = () => {
    toast.error("Please log in before contacting or revealing details");
    const scrollY = window.scrollY;
    navigate("/login", {
      state: {
        from: location.pathname,
        scrollY: scrollY
      }
    });
  };

  const payload = () => ({
    property_id: property.property_id,
    buyer_user_id: user?.user_id || user?.id,
    buyer_name: user?.name,
    buyer_phone: user?.phone,
    buyer_email: user?.email,
    message,
    contact_intent: "PROPERTY_ENQUIRY",
  });

  const checkPhoneVerification = (actionType) => {
    if (!user) {
      handleLoginRedirect();
      return false;
    }
    if (!user.phone_verified && !user.email_verified) {
      if (PHONE_AUTH_ENABLED) {
        setPostVerificationAction(actionType);
        setDialogPhone(user.phone || "");
        setDialogOtp("");
        setDialogOtpSent(false);
        setShowVerifyDialog(true);
        return false;
      }
      toast.error("Verify your email or phone before contacting a listing");
      return false;
    }
    return true;
  };

  const isRestrictedRole = user && ["admin", "super_admin", "agent", "office_user", "office"].includes(String(user.role).toLowerCase());

  const reveal = async () => {
    if (isUnavailable) {
      toast.error(unavailableReason);
      return;
    }
    if (!user) {
      handleLoginRedirect();
      return;
    }
    if (isRestrictedRole) {
      toast.error("This action is only intended for prospective buyers or owners.");
      return;
    }
    if (!checkPhoneVerification("reveal")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${property.property_id}/phone-reveal`, payload());
      setContact(data.contact || data);
      setRevealed(true);
      if (data?.already_exists) {
        toast.info(data.message || "We will contact you soon.");
      } else {
        toast.success("Query raised successfully.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const whatsapp = async () => {
    if (isUnavailable) {
      toast.error(unavailableReason);
      return;
    }
    if (!user) {
      handleLoginRedirect();
      return;
    }
    if (isRestrictedRole) {
      toast.error("This action is only intended for prospective buyers or owners.");
      return;
    }
    if (!checkPhoneVerification("whatsapp")) return;
    try {
      const { data } = await api.post(`/properties/${property.property_id}/whatsapp-click`, payload());
      if (data?.already_exists) {
        toast.info(data.message || "We will contact you soon.");
      } else {
        toast.success("WhatsApp event recorded");
      }
      const phone = (data?.contact?.phone || "").replace(/\D/g, "");
      if (phone) window.open(`https://wa.me/${phone}`, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDialogSendOtp = async (e) => {
    e.preventDefault();
    if (dialogPhone.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/otp/send", { phone: dialogPhone });
      setDialogOtpSent(true);
      toast.success("Verification OTP sent to your phone!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDialogVerifyOtp = async (e) => {
    e.preventDefault();
    if (dialogOtp.length !== 6) {
      toast.error("Please enter a 6-digit OTP code");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/verify-otp", {
        user_id: user.id || user.user_id,
        phone: dialogPhone,
        otp: dialogOtp
      });
      toast.success("Phone verified successfully!");
      const refreshedUser = await refreshUser();
      setShowVerifyDialog(false);
      
      // Proceed with original action
      if (postVerificationAction === "reveal") {
        setBusy(true);
        const customPayload = {
          property_id: property.property_id,
          buyer_name: refreshedUser?.name || user?.name,
          buyer_phone: dialogPhone,
          buyer_email: refreshedUser?.email || user?.email,
        };
        const { data } = await api.post(`/properties/${property.property_id}/phone-reveal`, customPayload);
        setContact(data.contact || data);
        setRevealed(true);
        toast.success("Query raised successfully.");
      } else if (postVerificationAction === "whatsapp") {
        const customPayload = {
          property_id: property.property_id,
          buyer_name: refreshedUser?.name || user?.name,
          buyer_phone: dialogPhone,
          buyer_email: refreshedUser?.email || user?.email,
        };
        const { data } = await api.post(`/properties/${property.property_id}/whatsapp-click`, customPayload);
        toast.success("WhatsApp event recorded");
        const phone = (data?.contact?.phone || "").replace(/\D/g, "");
        if (phone) window.open(`https://wa.me/${phone}`, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "OTP verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="listing-agent-lead" className="scroll-mt-28 premium-card bg-background border border-border rounded-2xl p-6" data-testid="contact-privacy-card">
      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">Listed by</p>
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-secondary overflow-hidden shrink-0 grid place-items-center">
          {agent?.picture ? <img src={agent.picture} alt={agent.name} loading="lazy" decoding="async" className="w-full h-full object-cover"/> : <UserCheck className="w-6 h-6 text-muted-foreground" />}
        </div>
        <div className="min-w-0">
          <p className="font-display font-bold text-primary">{agent?.name || property.owner_name || "Property contact"}</p>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{agent?.verified_owner ? "Verified listing agent" : agent ? "Listing agent" : "Owner/agent details hidden"}</p>
          {office && <p className="text-sm text-primary mt-1 flex items-center gap-1"><Building2 className="w-3 h-3"/>{office.office_name || office.name}</p>}
          {agent?.user_id && <Link to={`/agents/${agent.user_id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">View agent profile <ArrowRight className="h-3 w-3" /></Link>}
        </div>
      </div>
      <div className="mt-5 rounded-2xl bg-secondary/60 p-4">
        <p className="flex items-center gap-2 text-sm font-bold text-primary"><LockKeyhole className="w-4 h-4" /> Agent contact stays private</p>
        <p className="mt-2 text-sm text-muted-foreground">Your verified account details are attached only after a lead event is stored.</p>
        {isUnavailable && <p className="mt-3 text-xs font-bold text-warning-foreground">{unavailableReason}</p>}
      </div>

      <div className="mt-5 space-y-3">
        <div><Label htmlFor="lead-buyer-name">Your name</Label><Input id="lead-buyer-name" value={user?.name || ""} readOnly placeholder="Sign in to contact the agent" /></div>
        <div><Label htmlFor="lead-buyer-phone">Phone</Label><Input id="lead-buyer-phone" value={user?.phone || ""} readOnly placeholder="Your verified phone" /></div>
        <div><Label htmlFor="lead-buyer-email">Email</Label><Input id="lead-buyer-email" type="email" value={user?.email || ""} readOnly placeholder="Your verified email" /></div>
        <div><Label htmlFor="lead-message">Message</Label><Textarea id="lead-message" value={message} onChange={(event) => setMessage(event.target.value)} rows={3} maxLength={500} /></div>
      </div>

      <div className="mt-5 space-y-2 text-sm animate-fade-in">
        {revealed ? (
          <div className="p-3.5 bg-emerald-950/20 text-emerald-300 border border-emerald-900/40 rounded-xl text-xs font-semibold leading-relaxed">
            Your query has been raised, soon our agent will contact you.
          </div>
        ) : (
          <div className="flex items-center gap-2 font-semibold text-primary"><Phone className="w-4 h-4" />{masked}</div>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button onClick={reveal} disabled={busy || revealed || isRestrictedRole || isUnavailable} className="font-bold gap-2" data-testid="reveal-phone-btn"><MessageCircle className="w-4 h-4" />{revealed ? "Query raised" : busy ? "Creating lead…" : "Contact Agent"}</Button>
        <Button onClick={whatsapp} disabled={isRestrictedRole || isUnavailable} variant="outline" className="font-bold gap-2" data-testid="whatsapp-click-btn"><MessageCircle className="w-4 h-4" />WhatsApp</Button>
      </div>
      <div className="mt-2">
        <SiteVisitDialog property={property} open={bookingOpen} setOpen={setBookingOpen} unavailableReason={unavailableReason} />
      </div>

      {/* Phone Verification Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent data-testid="phone-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2 text-primary">
              <Phone className="w-6 h-6"/> Verify Mobile Number
            </DialogTitle>
            <DialogDescription>
              We require a verified mobile number before connecting you with property listings to ensure platform safety.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={dialogOtpSent ? handleDialogVerifyOtp : handleDialogSendOtp} className="space-y-4 pt-4">
            {!dialogOtpSent ? (
              <div className="space-y-1.5">
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  value={dialogPhone}
                  onChange={(e) => setDialogPhone(e.target.value)}
                  placeholder="+919999999999"
                  required
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>6-Digit Verification Code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  value={dialogOtp}
                  onChange={(e) => setDialogOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="text-center font-bold tracking-[0.5em] text-lg"
                  required
                />
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowVerifyDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy} className="font-bold">
                {busy ? "Processing…" : (dialogOtpSent ? "Verify & Proceed" : "Send OTP")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SiteVisitDialog({ property, open, setOpen, unavailableReason }) {
  const { user } = useAuth();
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm({ defaultValues: { buyer_name: user?.name || "", buyer_email: user?.email || "", buyer_phone: user?.phone || "", date: new Date(Date.now() + 86400000).toISOString().slice(0,10), time_slot: TIME_SLOTS[0], note: "" } });
  const isUnavailable = !!unavailableReason;
  const onSubmit = async (values) => {
    if (isUnavailable) { toast.error(unavailableReason); return; }
    if (!user) { toast.error("Please log in to book a site visit"); return; }
    const role = String(user.role || "").toLowerCase().replace("office_user", "office");
    if (!["buyer", "owner"].includes(role)) {
      toast.error("Site visits can be booked from a buyer or owner account.");
      return;
    }
    try {
      await api.post("/appointments", {
        ...values,
        buyer_user_id: user?.user_id || user?.id,
        property_id: property.property_id,
        visit_at: `${values.date}T${slotTo24h(values.time_slot)}:00`
      });
      toast.success("Site visit requested! Agent/office will confirm shortly.");
      reset(); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button variant="outline" disabled={isUnavailable} className="w-full font-bold" data-testid="open-appointment-btn"><Calendar className="w-4 h-4 mr-2"/>Schedule Site Visit</Button></DialogTrigger>
        <DialogContent data-testid="appointment-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Book a property visit</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={handleSubmit(onSubmit)}>
            <div><Label>Your Name</Label><Input {...register("buyer_name", { required: true })} data-testid="appt-name"/></div>
            <div><Label>Email</Label><Input type="email" {...register("buyer_email")} data-testid="appt-email"/></div>
            <div><Label>Phone</Label><Input {...register("buyer_phone", { required: true })} data-testid="appt-phone"/></div>
            <div className="grid grid-cols-2 gap-2"><div><Label>Date</Label><Input type="date" min={new Date().toISOString().slice(0, 10)} {...register("date", { required: true })} data-testid="appt-date"/></div><div><Label>Time</Label><select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" {...register("time_slot", { required: true })} data-testid="appt-time">{TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}</select></div></div>
            <div><Label>Notes</Label><Textarea {...register("note")} rows={2} data-testid="appt-notes"/></div>
            <Button type="submit" disabled={isSubmitting} className="w-full font-bold" data-testid="appt-submit">{isSubmitting ? "Booking…" : "Confirm Visit Request"}</Button>
          </form>
        </DialogContent>
      </Dialog>
  );
}

function LandLegalDetails({ land }) {
  const rows = [
    ["Facing", land.facing], ["Road Width", land.road_width_ft ? `${land.road_width_ft} ft` : null], ["Corner Plot", yesNo(land.corner_plot)],
    ["Registry Available", yesNo(land.registry_available)], ["Mutation Available", yesNo(land.mutation_available)], ["Ownership Type", land.ownership_type],
    ["Khatiyan", land.khatiyan], ["Jamabandi", land.jamabandi], ["Khasra Number", land.khasra_number], ["Survey Number", land.survey_number],
    ["Boundary Wall", yesNo(land.boundary_wall)], ["Gated Colony", yesNo(land.gated_colony)], ["Electricity", yesNo(land.electricity)], ["Water", yesNo(land.water)], ["Approach Road", land.approach_road_type],
  ];
  return <DetailGrid rows={rows} testid="land-legal-details" />;
}
function BuildingDetails({ building }) {
  return <DetailGrid rows={[["Bedrooms", building.bedrooms], ["Bathrooms", building.bathrooms], ["Balconies", building.balconies], ["Furnishing", building.furnishing], ["Property Age", building.property_age], ["Floor", building.floor_number], ["Total Floors", building.total_floors], ["Lift", yesNo(building.lift)], ["Parking", yesNo(building.parking)]]} testid="building-details" />;
}
function DetailGrid({ rows, testid }) {
  const filteredRows = rows.filter(([_, value]) => value !== null && value !== undefined && value !== "" && value !== "—");
  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid={testid}>
      {filteredRows.map(([label, value]) => (
        <Detail key={label} label={label} value={value} />
      ))}
    </div>
  );
}
function NearbyInfo({ nearby }) {
  const entries = Object.entries(nearby || {});
  if (!entries.length) return <p className="text-muted-foreground">No nearby info available.</p>;
  return <div className="grid sm:grid-cols-2 gap-6">{entries.map(([k, v]) => <div key={k} className="border border-border rounded-xl p-4"><p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{k}</p><ul className="text-sm text-primary space-y-1">{(v || []).map((it, i) => <li key={i}>· {it}</li>)}</ul></div>)}</div>;
}

// Animated key-fact tile below the gallery: gradient icon chip with a subtle bob + pan on hover.
function BigStat({ value, label }) {
  return (
    <div>
      <p className="font-display text-xl sm:text-2xl font-black text-foreground leading-none tabular-nums">{value}</p>
      <p className="mt-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function Spec({ icon: Icon, v, l }) { return <div className="group text-center"><span className="mx-auto mb-1.5 inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary shadow-sm transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 animate-gradient-pan"><Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px] group-hover:animate-icon-bob"/></span><p className="font-display font-bold text-primary text-xs sm:text-base">{v}</p><p className="text-[8px] sm:text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p></div>; }
function ExpandableText({ text, limit = 320 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <p className="text-muted-foreground">No description added yet.</p>;
  const isLong = text.length > limit;
  const shown = expanded || !isLong ? text : `${text.slice(0, limit).trimEnd()}\u2026`;
  return (
    <div>
      <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{shown}</p>
      {isLong && (
        <button type="button" onClick={() => setExpanded((value) => !value)} className="mt-2 text-sm font-bold text-primary hover:underline" data-testid="overview-toggle-description">
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
function Detail({ label, value }) { return <div className="group rounded-xl border border-border/80 bg-secondary/10 p-2.5 sm:p-4 hover:border-primary/30 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"><p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p><p className="font-display font-bold text-primary text-xs sm:text-sm mt-1 truncate" title={value}>{value}</p></div>; }

function ReviewSection({ propertyId, initial }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = String(user?.role || "").toLowerCase();
  const canReview = role === "buyer" || role === "owner";
  const [reviews, setReviews] = useState(initial || []);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!user) { toast.error("Please log in to review"); return; }
    if (comment.trim().length < 3) { toast.error("Please add at least 3 characters"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { property_id: propertyId, rating, comment });
      toast.success("Review submitted for moderation");
      setComment("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-6">
      {canReview ? <div className="border border-border rounded-2xl p-4">
        <p className="font-display font-bold text-primary">Write a review</p>
        <p className="text-xs text-muted-foreground mt-1">Published after a trust-and-safety review.</p>
        <div className="flex items-center gap-2 mt-2 mb-3">{[1,2,3,4,5].map(n => <button key={n} type="button" title={`Rate ${n} out of 5`} aria-label={`Rate ${n} out of 5`} aria-pressed={rating === n} onClick={() => setRating(n)} data-testid={`rate-${n}`}><Star className={`w-6 h-6 ${n <= rating ? "fill-primary text-primary" : "text-muted-foreground"}`}/></button>)}</div>
        <Textarea rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" data-testid="review-comment"/>
        <Button onClick={submit} disabled={submitting} title="Submit this review for moderation" className="mt-3 font-bold" data-testid="review-submit">{submitting ? "Submitting…" : "Submit Review"}</Button>
      </div> : <div className="border border-dashed border-border rounded-2xl p-5 bg-secondary/30">
        <p className="font-display font-bold text-primary">Reviews require an account</p>
        <p className="text-xs text-muted-foreground mt-1">Sign in with a buyer or owner account to submit feedback for moderation.</p>
        {!user && <Button variant="outline" onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/properties/${propertyId}`)}`)} title="Sign in to write a review" className="mt-3">Sign in to review</Button>}
      </div>}
      {reviews.length === 0 ? <p className="text-muted-foreground">No reviews yet. Be the first!</p> : <div className="space-y-3">{reviews.map((r, i) => {
        const numericRating = Number(r.rating);
        const safeRating = Number.isFinite(numericRating) && numericRating >= 1 && numericRating <= 5 ? Math.round(numericRating) : 0;
        return <div key={r.review_id || r.id || i} className="border border-border rounded-2xl p-4"><div className="flex items-center justify-between gap-3"><p className="font-display font-bold text-primary">{r.user_name || "Anonymous reviewer"}</p>{safeRating ? <div className="flex gap-0.5" aria-label={`${safeRating} out of 5 stars`}>{[...Array(safeRating)].map((_,j) => <Star key={j} className="w-3 h-3 fill-primary text-primary"/>)}</div> : <span className="text-xs text-muted-foreground">Rating unavailable</span>}</div><p className="text-sm text-muted-foreground mt-2">{r.comment}</p></div>;
      })}</div>}
    </div>
  );
}

function DetailSkeleton() { return <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8"><div className="h-8 w-40 shimmer rounded mb-6"/><div className="h-[420px] shimmer rounded-3xl"/><div className="grid lg:grid-cols-12 gap-10 mt-10"><div className="lg:col-span-8 space-y-4"><div className="h-12 shimmer rounded"/><div className="h-28 shimmer rounded"/><div className="h-64 shimmer rounded"/></div><div className="lg:col-span-4 h-96 shimmer rounded-3xl"/></div></div>; }
const PROPERTY_IMAGE_FALLBACK = "/brand/real-place-mark.svg";
function propertyImageClass(image, extra = "") {
  const fit = image === PROPERTY_IMAGE_FALLBACK ? "object-contain bg-slate-100 p-10" : "object-cover";
  return `w-full h-full ${fit} ${extra}`.trim();
}
function normalizeImages(p) { 
  const imgs = Array.isArray(p.images) ? p.images.map((x) => {
    let url = typeof x === "string" ? x : x.url || x.public_url;
    if (url && url.startsWith("/")) {
      url = `${BACKEND}${url}`;
    }
    return url;
  }).filter(Boolean) : []; 
  return imgs.length ? imgs : [PROPERTY_IMAGE_FALLBACK];
}
function formatDate(value) { const date = value ? new Date(value) : null; return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Date unavailable"; }
function yesNo(v) { return v === true || v === "true" ? "Yes" : v === false || v === "false" ? "No" : v; }
function maskPhone(phone = "") { const p = String(phone); if (p.length < 6) return "Login to reveal"; return p.replace(/(\d{2})\d+(\d{2})$/, "$1xxxxxx$2"); }
function slotTo24h(slot) { if (!slot) return "10:00"; const [time, mod] = slot.split(" "); let [h, m] = time.split(":").map(Number); if (mod === "PM" && h !== 12) h += 12; if (mod === "AM" && h === 12) h = 0; return `${String(h).padStart(2,"0")}:${String(m || 0).padStart(2,"0")}`; }
